import React from 'react';

import { ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import { Alert, AlertTitle, Box, Button, InputAdornment, Stack, TextField } from '@mui/material';

import { useNotification } from 'src/hooks/app';

type CredentialFieldProps = {
  label: string;
  value: string;
  onCopy: () => void;
};

const CredentialField: React.FC<CredentialFieldProps> = ({ label, value, onCopy }) => {
  return (
    <TextField
      value={value}
      fullWidth
      label={label}
      slotProps={{
        input: {
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <Button
                variant="outlined"
                size="small"
                startIcon={<ContentCopyIcon fontSize="small" />}
                onClick={onCopy}
              >
                Copy
              </Button>
            </InputAdornment>
          ),
        },
      }}
    />
  );
};

type CredentialsViewProps = {
  alertTitle: string;
  alertBody: React.ReactNode;
  clientId: string;
  clientSecret: string;
  onClose: () => void;
  closeLabel?: string;
};

export const CredentialsView: React.FC<CredentialsViewProps> = ({
  alertTitle,
  alertBody,
  clientId,
  clientSecret,
  onClose,
  closeLabel = "I've saved these",
}) => {
  const { spawnNotification } = useNotification();

  const copy = React.useCallback(
    (label: string, value: string) => {
      navigator.clipboard.writeText(value).then(
        () => spawnNotification(`${label} copied to clipboard`, 'success', 'Copied'),
        () => spawnNotification(`Failed to copy ${label}`, 'error', 'Error'),
      );
    },
    [spawnNotification],
  );

  return (
    <Stack spacing={3}>
      <Alert severity="warning" sx={{ alignItems: 'flex-start' }}>
        <AlertTitle sx={{ fontWeight: 700 }}>{alertTitle}</AlertTitle>
        {alertBody}
      </Alert>

      <CredentialField
        label="Client ID"
        value={clientId}
        onCopy={() => copy('Client ID', clientId)}
      />
      <CredentialField
        label="Client Secret"
        value={clientSecret}
        onCopy={() => copy('Client Secret', clientSecret)}
      />

      <Box>
        <Button variant="contained" onClick={onClose}>
          {closeLabel}
        </Button>
      </Box>
    </Stack>
  );
};
